import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import { DRAFT } from '../../../constants/draft.constants';
import { USER } from '../../../constants/user.constants';
import ButtonComponent from '../Button/Button.component';
import './simple-dialog.scss';

export interface SimpleDialogProps {
  open: boolean;
  onClose: () => void;
  dialogBody: string | React.ReactNode;
  onSubmit?: () => void;
  onSubmitSources?: (sources: string[]) => void;
  onSubmitSourcesMode?: boolean;
  dialogTitle?: string;
  className?: string;
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  closeModalLabel?: string;
  actionLabel?: string;
  textEditor?: boolean;
  editedBody?: boolean;
  titleAreaClassName?: string;
  dialogTitleChildren?: any;
  data?: Record<string, unknown>;
}

export const SimpleDialog = (props: SimpleDialogProps): React.ReactElement => {
  const {
    data,
    onClose,
    onSubmit,
    open,
    dialogTitle,
    dialogBody,
    className,
    maxWidth = 'sm',
    closeModalLabel,
    actionLabel,
    textEditor,
    titleAreaClassName,
    dialogTitleChildren,
  } = props;

  const { t } = useTranslation('dialog');
  const { SAVE_ANALYSIS } = DRAFT.ANALYSIS_DIALOG;

  const maxWidthMap = {
    xs: '320px',
    sm: '480px',
    md: '640px',
    lg: '800px',
    xl: '960px',
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="SimpleDialog__Overlay" />
        <Dialog.Content
          className={`SimpleDialog__Content ${className ?? ''}`}
          style={{ maxWidth: maxWidthMap[maxWidth] }}
        >
          {(dialogTitle || dialogTitleChildren) && (
            <Dialog.Title
              className={`SimpleDialog__Title ${titleAreaClassName ?? ''}`}
            >
              {dialogTitle ? t(dialogTitle) : dialogTitleChildren}
            </Dialog.Title>
          )}
          <Dialog.Close asChild>
            <button
              type="button"
              className="SimpleDialog__CloseBtn"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </Dialog.Close>
          <div className="SimpleDialog__Body APP__Simple__Dialog__Body">
            {dialogBody}
          </div>
          <div className="SimpleDialog__Actions">
            <ButtonComponent
              variant={
                textEditor
                  ? 'dangerOutline'
                  : actionLabel === SAVE_ANALYSIS
                    ? 'outline'
                    : 'primary'
              }
              onClick={onClose}
            >
              {closeModalLabel}
            </ButtonComponent>
            {!textEditor &&
              !(data?.hasStoryAnalysis as boolean) &&
              actionLabel && (
                <ButtonComponent
                  type="submit"
                  variant={
                    actionLabel === USER.DELETE_ACCOUNT
                      ? 'danger'
                      : actionLabel === SAVE_ANALYSIS
                        ? 'primary'
                        : 'outline'
                  }
                  onClick={onSubmit}
                >
                  {actionLabel}
                </ButtonComponent>
              )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
